/**
 * Labstep
 */

import { GridSmartTableProtocolConditionColDefService } from 'labstep-web/grid/SmartTableProtocolCondition/services/grid-smart-table-protocol-condition-coldef.service';
import { GridSmartTableProtocolValueColDefService } from 'labstep-web/grid/SmartTableProtocolValue/services/grid-smart-table-protocol-value-coldef.service';
import type { APICallOptions } from 'labstep-web/models';
import { Experiment } from 'labstep-web/models/experiment.model';
import { Molecule } from 'labstep-web/models/molecule.model';
import { ProtocolCondition } from 'labstep-web/models/protocol-condition.model';
import { ProtocolValue } from 'labstep-web/models/protocol-value.model';
import { MENU_COMMANDS_ELEMENTS } from 'labstep-web/prosemirror/components/Menu/Commands/elements';
import { reactPropsKey } from 'labstep-web/prosemirror/extensions/external-comm';
import { loaderKey } from 'labstep-web/prosemirror/extensions/loader/plugin';
import type { IProseMirrorCommandsElement } from 'labstep-web/prosemirror/extensions/slash/types';
import type { IStateDispatchProps } from 'labstep-web/prosemirror/marks/types';
import { replaceWithNode } from 'labstep-web/prosemirror/nodes/commands';
import { getToken } from 'labstep-web/prosemirror/utils/selection';
import {
  PostFilterComparison,
  PostFilterOperator,
} from 'labstep-web/services/postFilter/constants';
import { ProsemirrorService } from 'labstep-web/services/prosemirror.service';
import { getIdAttribute } from 'labstep-web/services/schema/helpers';
import { generateTableData } from 'labstep-web/services/table.service';
import { createEntity } from 'labstep-web/state/actions/entity';
import store from 'labstep-web/state/store';
import type { EditorState } from 'prosemirror-state';
import { v4 } from 'uuid';

export class ProsemirrorSlashService {
  static TRIGGER = '/';

  static addPlaceholder(
    state: EditorState,
    dispatch: IStateDispatchProps['dispatch'],
    id: unknown,
  ): void {
    dispatch?.(
      state.tr.setMeta(loaderKey, {
        add: { id, pos: state.tr.selection.from },
      }),
    );
  }

  static removePlaceholder(
    state: EditorState,
    dispatch: IStateDispatchProps['dispatch'],
    id: unknown,
  ): void {
    dispatch?.(
      state.tr.setMeta(loaderKey, {
        remove: { id },
      }),
    );
  }

  /** Set placeholder and replace token with node */
  static updateViewOnCreate(
    state: EditorState,
    dispatch: IStateDispatchProps['dispatch'],
    key: string,
    addContent?: boolean,
  ): APICallOptions {
    const id = {};
    ProsemirrorSlashService.addPlaceholder(state, dispatch, id);

    const { from, to } = getToken(
      state,
      ProsemirrorSlashService.TRIGGER,
    );
    return {
      onSuccess: ({ response }) => {
        const result = Array.isArray(response.result)
          ? response.result[0]
          : response.result;

        let attrs = {
          [getIdAttribute(key)]: result,
        };
        if (key === 'protocol_well_plate') {
          attrs = {
            id: result,
          };
        }

        ProsemirrorSlashService.removePlaceholder(
          state,
          dispatch,
          id,
        );
        replaceWithNode(
          state,
          dispatch,
          from,
          to,
          key,
          attrs,
          addContent && state.schema.nodes.paragraph.createAndFill(),
        );
      },
      onFail: (): void =>
        ProsemirrorSlashService.removePlaceholder(
          state,
          dispatch,
          id,
        ),
    };
  }

  static createElement(
    state: EditorState,
    dispatch: IStateDispatchProps['dispatch'],
    element: IProseMirrorCommandsElement,
  ): void {
    const { key, entityName, onCreate, createBody } =
      MENU_COMMANDS_ELEMENTS.find((el) => el.id === element)!;

    if (onCreate) {
      onCreate(state, dispatch);
      return;
    }

    const reactPropsPlugin = state.plugins.find(
      (p) => p.spec.key === reactPropsKey,
    );

    if (!reactPropsPlugin || !entityName) {
      return;
    }

    let { entity } = reactPropsPlugin.getState(state);

    let body = {};
    let options: Record<string, unknown> = {
      additionalMeta: { redirect: true },
    };
    let addContent = false;

    if (element === 'protocol_table') {
      body = { name: 'Untitled Table', data: generateTableData() };
    } else if (['protocol_step'].includes(entityName)) {
      body = [{}];
      options = { ...options, batch: true };
      addContent = true;
    } else if (entityName === 'metadata') {
      body = { type: 'file', is_output: true, ...createBody };
      entity = entity.metadata_thread;
    } else if (entityName === 'protocol_value') {
      body = { ...createBody };
    } else if (entityName === 'molecule') {
      body = Molecule.createBodyDefault;
    } else if (element === 'smart_table_protocol_condition') {
      body = {
        name: `Smart Table # ${ProsemirrorService.countNodesByType(state, 'smart_table') + 1}`,
        context: 'smart_table',
        column_definition_ids:
          GridSmartTableProtocolConditionColDefService.getDefaultColIds(
            entity,
          ),
        group_id:
          entity instanceof Experiment
            ? entity.experiment_workflow.owner.guid
            : entity.author.group.guid,
        entity_name: ProtocolCondition.entityName,
        parameters: {
          filter: [
            {},
            {
              path:
                entity instanceof Experiment
                  ? 'experiment'
                  : 'protocol',
              predicates: [
                {
                  attribute: 'guid',
                  comparison: PostFilterComparison.eq,
                  value: entity.guid,
                },
              ],
            },
          ],
        },
      };
    } else if (element === 'smart_table_protocol_value') {
      body = {
        name: `Smart Table # ${ProsemirrorService.countNodesByType(state, 'smart_table') + 1}`,
        context: 'smart_table',
        column_definition_ids:
          GridSmartTableProtocolValueColDefService.getDefaultColIds(
            entity,
          ),
        group_id:
          entity instanceof Experiment
            ? entity.experiment_workflow.owner.guid
            : entity.author.group.guid,
        entity_name: ProtocolValue.entityName,
        parameters: {
          filter: [
            {},
            {
              type: PostFilterOperator.and,
              path: '',
              predicates: [
                {
                  attribute: 'name',
                  comparison: PostFilterComparison.contains,
                  value: [],
                },
              ],
            },
          ],
        },
      };
    }

    const additionalOptions =
      ProsemirrorSlashService.updateViewOnCreate(
        state,
        dispatch,
        key,
        addContent,
      );

    store.dispatch(
      createEntity(
        entityName,
        body,
        element.startsWith('smart_table_')
          ? undefined
          : entity.entityName,
        element.startsWith('smart_table_') ? undefined : entity.id,
        v4(),
        {
          ...options,
          ...additionalOptions,
        },
      ),
    );
  }
}
